import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { selectPceSearchData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import useSecondsUntil from '@/shared/hooks/useSecondsUntil'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import { blockedFailureReason, canSearchWithName, isSerialNumberInvalid, streetNumberDigits } from './pceSearchRules'
import type { PceFailedSearch, PceSearchBlockReason, PceSearchFailureReason, PceSearchParams } from './pceSearchRules'
import { PceSearchCountdownOverrides, PulsePceSearchCountdownOverrides } from './styled-components'

interface PceSearchBlock {
  reason: PceSearchBlockReason
  until: string
  totalSeconds: number
}

interface PceSearchState {
  address: { inseeCode?: string; streetNumber?: string; streetName?: string }
  occupant?: string
  serialNumber?: string
  lastFailedSearch: PceFailedSearch | null
  searchBlock: PceSearchBlock | null
}

interface BlockMessageInput {
  t: TFunction
  activeBlock: PceSearchBlock | null
  remainingSeconds: number
  failureReason: PceSearchFailureReason | null
  nameTooShort: boolean
}

const BLOCKING_KEY = 'subscriptionTools:pce_search.blocking'

const resolveBlockMessage = ({
  t,
  activeBlock,
  remainingSeconds,
  failureReason,
  nameTooShort,
}: BlockMessageInput): string | null => {
  if (activeBlock?.reason === 'rate_limited') return t(`${BLOCKING_KEY}.rate_limited`, { seconds: remainingSeconds })
  if (activeBlock?.reason === 'grdf_unavailable') return t(`${BLOCKING_KEY}.grdf_unavailable`)
  if (failureReason !== null) return t(`${BLOCKING_KEY}.${failureReason}`)
  if (nameTooShort) return t(`${BLOCKING_KEY}.name_too_short`)

  return null
}

const usePceSearchBlock = () => {
  const { t } = useTranslation()
  const isPulse = usePulseScope()
  const { address, occupant, serialNumber, lastFailedSearch, searchBlock }: PceSearchState =
    useSelector(selectPceSearchData)

  const params: PceSearchParams = {
    inseeCode: address.inseeCode ?? '',
    streetNumber: streetNumberDigits(address.streetNumber),
    streetName: address.streetName ?? '',
    occupant: occupant ?? '',
    serialNumber: serialNumber ?? '',
  }

  const remainingSeconds = useSecondsUntil(searchBlock?.until ?? null)
  const activeBlock = remainingSeconds > 0 ? searchBlock : null
  const failureReason = blockedFailureReason(params, lastFailedSearch)
  const serialNumberInvalid = isSerialNumberInvalid(params.serialNumber)
  const nameAllowsSearch = canSearchWithName(params)
  const nameTooShort = params.occupant !== '' && !nameAllowsSearch
  const hasAddress = Object.keys(address).length > 0

  return {
    isSearchBlocked: activeBlock !== null || failureReason !== null || serialNumberInvalid || !nameAllowsSearch,
    blockMessage: resolveBlockMessage({ t, activeBlock, remainingSeconds, failureReason, nameTooShort }),
    countdown:
      activeBlock?.reason === 'rate_limited' ? { value: remainingSeconds, maxValue: activeBlock.totalSeconds } : null,
    countdownOverrides: isPulse ? PulsePceSearchCountdownOverrides : PceSearchCountdownOverrides,
    serialNumberError: serialNumberInvalid ? t(`${BLOCKING_KEY}.invalid_serial_number`) : null,
    streetNumberHint: hasAddress && params.streetNumber.trim() === '' ? t(`${BLOCKING_KEY}.street_number_hint`) : null,
    isPulse,
  }
}

export default usePceSearchBlock
